import React, { useState } from 'react';
import { Sparkles, Code2, Copy, Check, CheckCircle } from 'lucide-react';

const AiFixesPanel = ({ project, apiFetch, showNotification }) => {
  const [generatingId, setGeneratingId] = useState(null);
  const [fixes, setFixes] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [appliedIds, setAppliedIds] = useState({});

  const findings = project?.technical_audit?.findings || [];
  const robotsStatus = project?.robots_status || project?.crawl_overview?.robots_txt_status || 'Missing';
  const sitemapStatus = project?.sitemap_status || project?.crawl_overview?.sitemap_status || 'Missing';

  const fixableItems = [];

  if (!robotsStatus.includes('Found') && !robotsStatus.includes('Valid')) {
    fixableItems.push({
      id: 'robots_txt',
      type: 'Technical',
      title: 'Missing or Invalid Robots.txt File',
      description: 'Site is missing a standard robots.txt file to guide search engine crawlers.',
      url: `${project.url}/robots.txt`,
      recommendation: 'Generate and upload a production-ready robots.txt file.'
    });
  }

  if (!sitemapStatus.includes('Found') && !sitemapStatus.includes('Valid')) {
    fixableItems.push({
      id: 'sitemap_xml',
      type: 'Technical',
      title: 'Missing XML Sitemap',
      description: 'Site is missing an XML sitemap for search engines to discover all URLs.',
      url: `${project.url}/sitemap.xml`,
      recommendation: 'Generate an XML sitemap index listing key domain pages.'
    });
  }

  findings.forEach((f, idx) => {
    fixableItems.push({
      id: `finding_${idx}`,
      type: f.category || 'SEO Issue',
      title: `${f.metric}: ${f.current_value}`,
      description: f.business_impact || f.recommendation,
      url: f.evidence?.[0]?.url || project.url,
      recommendation: f.recommendation
    });
  });

  const handleGenerateFix = async (item) => {
    setGeneratingId(item.id);
    try {
      const payload = {
        finding: item.recommendation ? `${item.title}. Recommendation: ${item.recommendation}` : item.title,
        url: item.url,
        page_info: {
          title: item.title,
          url: item.url
        }
      };

      const res = await apiFetch('/seo/generate-fix', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setFixes(prev => ({ ...prev, [item.id]: res }));
    } catch (err) {
      showNotification('Could not generate AI fix: ' + err.message, 'error');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleApplyFix = async (item, fixResult) => {
    setApplyingId(item.id);
    try {
      const fixType = item.id === 'robots_txt' ? 'robots_txt' : item.id === 'sitemap_xml' ? 'sitemap_xml' : 'custom';
      const fileLoc = fixResult.file_location || (
        item.id === 'robots_txt' ? 'public/robots.txt' :
        item.id === 'sitemap_xml' ? 'public/sitemap.xml' :
        'public/index.html'
      );
      await apiFetch('/seo/apply-fix', {
        method: 'POST',
        body: JSON.stringify({
          project_url: project?.url,
          fix_type: fixType,
          file_path: fileLoc,
          content: fixResult.fixed_code
        })
      });
      setAppliedIds(prev => ({ ...prev, [item.id]: true }));
      showNotification(`🎉 Successfully applied fix to ${project?.url}!`, 'success');
    } catch (err) {
      showNotification('Could not apply fix: ' + err.message, 'error');
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 p-5 rounded-2xl flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h3 className="text-base font-extrabold text-white flex items-center gap-2">
            <Sparkles className="text-purple-400 w-5 h-5 animate-pulse" />
            AI Code Fix Generator
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Instantly generate production-ready HTML, metadata tags, robots.txt, and sitemap code fixes for all identified SEO issues.
          </p>
        </div>
        <span className="bg-purple-950/60 border border-purple-500/40 text-purple-300 text-xs px-3 py-1.5 rounded-full font-mono font-bold shrink-0">
          {fixableItems.length} Fixes Available
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {fixableItems.map((item) => {
          const isGenerating = generatingId === item.id;
          const fixResult = fixes[item.id];

          return (
            <div key={item.id} className="bg-black/30 border border-gray-800 hover:border-gray-700 rounded-2xl p-5 transition-all flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-purple-950/40 border border-purple-500/30 text-purple-400 mt-0.5">
                    <Code2 size={16} />
                  </div>
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 font-bold bg-purple-950/50 px-2 py-0.5 rounded border border-purple-500/20">
                      {item.type}
                    </span>
                    <h4 className="text-sm font-bold text-white mt-1">{item.title}</h4>
                    <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>
                  </div>
                </div>

                <button
                  onClick={() => handleGenerateFix(item)}
                  disabled={isGenerating}
                  className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg shadow-purple-900/20 transition-all shrink-0 self-start sm:self-center"
                >
                  {isGenerating ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating Code...</>
                  ) : fixResult ? (
                    <><Sparkles size={14} /> Re-Generate Fix</>
                  ) : (
                    <><Sparkles size={14} /> Generate AI Code Fix</>
                  )}
                </button>
              </div>

              {/* GENERATED FIX CODE DISPLAY */}
              {fixResult && (
                <div className="bg-gray-950 border border-purple-900/40 rounded-xl p-4 flex flex-col gap-3 animate-in fade-in duration-200">
                  <div className="flex justify-between items-center border-b border-gray-850 pb-2">
                    <span className="text-xs font-bold text-purple-300 flex items-center gap-1.5">
                      <CheckCircle size={14} className="text-green-400" />
                      Production-Ready Code Replacement
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopy(fixResult.fixed_code, item.id)}
                        className="bg-gray-900 hover:bg-gray-800 text-gray-300 text-xs px-2.5 py-1 rounded-lg border border-gray-700 flex items-center gap-1.5 transition-all"
                      >
                        {copiedId === item.id ? (
                          <><Check size={13} className="text-green-400" /> Copied!</>
                        ) : (
                          <><Copy size={13} /> Copy Code</>
                        )}
                      </button>
                      <button
                        onClick={() => handleApplyFix(item, fixResult)}
                        disabled={applyingId === item.id || appliedIds[item.id]}
                        className={`text-xs px-3 py-1 rounded-lg border flex items-center gap-1.5 font-semibold transition-all ${
                          appliedIds[item.id]
                            ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                            : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white border-emerald-500/50 shadow-md shadow-emerald-900/30'
                        }`}
                      >
                        {applyingId === item.id ? (
                          <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Deploying...</>
                        ) : appliedIds[item.id] ? (
                          <><CheckCircle size={13} className="text-emerald-400" /> Applied to Site</>
                        ) : (
                          <><Sparkles size={13} /> 🚀 Apply to Website</>
                        )}
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const fileLoc = fixResult.file_location || (
                      item.id === 'robots_txt' ? 'public/robots.txt' :
                      item.id === 'sitemap_xml' ? 'public/sitemap.xml' :
                      'Inside the <head> tag of index.html or app/layout.tsx'
                    );
                    return (
                      <div className="bg-purple-950/60 border border-purple-500/40 px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-inner">
                        <span className="text-xs font-bold text-purple-300 flex items-center gap-1">
                          📂 File Location:
                        </span>
                        <span className="text-xs font-mono font-bold text-green-300 bg-black/60 px-2 py-0.5 rounded border border-purple-500/30">
                          {fileLoc}
                        </span>
                      </div>
                    );
                  })()}

                  {fixResult.explanation && (
                    <p className="text-xs text-gray-400 italic">
                      💡 Rationale: {fixResult.explanation}
                    </p>
                  )}

                  <pre className="bg-black/90 p-3 rounded-lg text-xs font-mono text-green-300 overflow-x-auto border border-gray-850 custom-scrollbar max-h-60">
                    <code>{fixResult.fixed_code}</code>
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AiFixesPanel;
